import {
  Euler,
  LoadingManager,
  Mesh,
  sRGBEncoding,
  TextureLoader,
  Object3D,
  Group,
  RepeatWrapping,
  Box3,
  MeshBasicMaterial,
  BufferGeometry,
} from "three";
import { DRACOLoader, GLTF, GLTFLoader, KTX2Loader } from "three-stdlib";
import { GPULevel } from "../../util/benchmark";

import { DEFAULT_ASSET_BASE } from "../../constants";
import { Context } from "../../context";
import { defaultScale } from "../../util/models";
import { SceneObjectBase } from "../object";
import { Collider } from "./collider";
import { StadiumGroundFallback } from "./ground";
import { teamAbbrevToId, teamIdToAbbrev } from "../../../lib/types/statsApi";
import { Crowd } from "./crowd";
import { PoserClock } from "../../util/clock";

const DEFAULT_STADIUM_URL = `${DEFAULT_ASSET_BASE}/models/ballparks/Stadium_LAD.glb`;

// load universal basis gpu compressed textures
const texLoader = new KTX2Loader();
const dracoLoader = new DRACOLoader();

const loadManager = new LoadingManager();
loadManager.addHandler(/\.ktx2$/, texLoader);

const gltfLoader = new GLTFLoader(loadManager);
gltfLoader.setKTX2Loader(texLoader);
gltfLoader.setDRACOLoader(dracoLoader);

const textureLoader = new TextureLoader();

export class Stadium extends SceneObjectBase {
  fallback = new StadiumGroundFallback();

  collider?: Collider;
  box?: Box3;
  loaded: boolean = false;

  crowd?: Crowd;

  private group = new Group();
  private basePath: string | undefined = undefined;

  constructor() {
    super();

    this.group.add(this.fallback.object);
  }

  /**
   *
   * @param context
   * @param venueAbbr
   * @param path used for drop-in - specifies local file
   * @returns
   */
  async init(context: Context, data?: string) {
    const { options, scene, teamAndPlayerInfo } = context;
    const assetBase = options.localBallparks
      ? options.assetBase
      : DEFAULT_ASSET_BASE;

    this.basePath = `${assetBase}/models/ballparks`;

    dracoLoader.setDecoderPath(`${DEFAULT_ASSET_BASE}/libs/draco/`);

    this.fallback.object.visible = true;
    scene.add(this.fallback.object);

    let gltf: GLTF;
    let path: string | undefined = undefined;

    const homeId = options.homeOverride
      ? teamAbbrevToId[options.homeOverride]
      : teamAndPlayerInfo.getHomeTeam()?.team.id || 0;
    const awayId = options.awayOverride
      ? teamAbbrevToId[options.awayOverride]
      : teamAndPlayerInfo.getAwayTeam()?.team.id || 0;

    const homeAbbr = options.homeOverride ?? teamIdToAbbrev[homeId];
    const awayAbbr = options.awayOverride ?? teamIdToAbbrev[awayId];

    if (data) {
      gltf = await gltfLoader.loadAsync(data);
    } else {
      if (homeAbbr == undefined) {
        console.error(`no team id for ${homeId}`);
        path = this.getFallback();
      } else {
        path = `${this.basePath}/Stadium_${homeAbbr}.glb`;

        const headResp = await fetch(path, { method: "HEAD" });

        path = headResp.status !== 200 ? this.getFallback() : path;
      }

      gltf = await gltfLoader.loadAsync(path);
    }

    this.processGLTF(context, gltf, homeAbbr, awayAbbr);
    console.log("Done loading stadium from ", path ?? "drop-in");

    if (!this.collider) {
      console.error("no collider for stadium ", path ?? "drop-in");
    }
    this.loaded = true;
  }

  update(clock: PoserClock) {
    this.crowd?.update(clock);
  }

  get object(): Object3D {
    return this.group;
  }

  set visible(visible: boolean) {
    this.group.visible = visible;
  }

  get visible(): boolean {
    return this.group.visible;
  }

  private processGLTF(
    context: Context,
    gltf: GLTF,
    homeAbbr: string,
    awayAbbr: string
  ) {
    const stadium = gltf.scene;
    this.box?.setFromObject(stadium);

    this.group.clear();
    this.group.add(stadium);

    this.addCrowd(stadium, context, homeAbbr, awayAbbr);

    stadium.traverse((obj: Object3D) => {
      obj.receiveShadow = true;
      obj.castShadow = true;
      if (obj instanceof Mesh) {
        // context.world?.lights.csm?.setupMaterial(obj.material);

        // use basic material if on low spec device
        if (context.options.gpuLevel != GPULevel.HIGH) {
          const old = obj.material;
          const material = new MeshBasicMaterial();
          material.copy(obj.material);
          obj.material = material;
          old.dispose();
        }

        if (obj.material.name.toLowerCase().includes("field")) {
          obj.material.roughness = 1;
        }

        if (obj.material.name == "AdMaster") {
          const tex = textureLoader.load(`${this.basePath}/Billboards.jpg`);
          tex.encoding = sRGBEncoding;
          tex.flipY = false;

          tex.wrapS = RepeatWrapping;

          obj.material.map = tex;
        }
      }
    });

    stadium.scale.set(defaultScale, defaultScale, defaultScale);
    stadium.visible = context.state.stadium;

    this.collider = Collider.fromGLTF(gltf);

    this.fallback.object.visible = false;
  }

  private async addCrowd(
    lookIn: Object3D,
    context: Context,
    homeAbbr: string,
    awayAbbr: string,
    separate = false
  ) {
    const crowdGeom = lookIn.getObjectByName("Crowd");

    if (
      crowdGeom &&
      crowdGeom instanceof Mesh &&
      crowdGeom.geometry instanceof BufferGeometry
    ) {
      console.log(`adding crowd`);

      if (separate) {
        crowdGeom.scale.set(defaultScale, defaultScale, defaultScale);
      }

      this.crowd = new Crowd(context, crowdGeom, homeAbbr, awayAbbr);
      this.group.add(this.crowd.object);
    } else if (!separate) {
      console.log("fetching separate crowd geometry");
      const crowdGltf = await this.fetchCrowd(homeAbbr);
      if (crowdGltf) {
        this.addCrowd(crowdGltf.scene, context, homeAbbr, awayAbbr, true);
      }
    } else {
      console.error("unable to get crowd geometry");
    }
  }

  private async fetchCrowd(homeAbbr: string): Promise<GLTF | undefined> {
    // fetch separate crowd file:
    const crowdPath = `${this.basePath}/crowd/Stadium_${homeAbbr}-crowd.glb`;

    const headResp = await fetch(crowdPath, { method: "HEAD" });
    if (headResp.status !== 200) {
      console.error("can't fetch separate crowd geometry");
      return undefined;
    }

    return await gltfLoader.loadAsync(crowdPath);
  }

  private getFallback(): string {
    console.warn("Using fallback stadium:", DEFAULT_STADIUM_URL);
    return DEFAULT_STADIUM_URL;
  }
}
